import { useGetProfitTimeseriesQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * DailyPnLCard — today's realized PnL across the forward-earning
 * engines, straight from /api/profit/timeseries (same query the
 * motivation chart uses, so RTK Query serves both from one request).
 *
 * Big signed number for the day total + small per-source chips
 * (grid / scalp / inv-swing / hourly). Alpha is excluded by the
 * endpoint by default — the AlphaLossCapPill covers that engine.
 */

interface ProfitDay {
  day: string;
  total: number;
  cumulative: number;
  // per-source keys (grid, scalp, inv_swing, hourly, ...) ride along
  [source: string]: number | string;
}

interface ProfitTimeseries {
  days?: ProfitDay[];
  total_realized?: number;
  n_events?: number;
  alpha_excluded?: boolean;
  by_source_total?: Record<string, number>;
}

const SOURCE_LABELS: Record<string, string> = {
  grid: "grid",
  scalp: "scalp",
  inv_swing: "inv-swing",
  hourly: "hourly",
  alpha: "alpha",
  dca: "dca",
};

const META_KEYS = new Set(["day", "total", "cumulative"]);

export function DailyPnLCard() {
  // Same args as the Overview motivation chart → deduped, no extra request.
  const profitTs = useGetProfitTimeseriesQuery(30, { pollingInterval: 60000 });
  const data = profitTs.data as ProfitTimeseries | undefined;

  // The endpoint groups by UTC date (sqlite datetime('now')).
  const todayUtc = new Date().toISOString().slice(0, 10);
  const today = (data?.days ?? []).find((d) => d.day === todayUtc);
  const total = Number(today?.total ?? 0);
  const hasFills = !!today && Math.abs(total) > 0.0001;

  const chips = today
    ? Object.entries(today)
        .filter(([k, v]) => !META_KEYS.has(k) && typeof v === "number" && Math.abs(v) > 0.0001)
        .map(([k, v]) => [k, Number(v)] as const)
        .sort((a, b) => b[1] - a[1])
    : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Today's realized P&L
          <span className="text-xs font-normal text-muted ml-2">
            {todayUtc} UTC · grid + scalp + inv-swing + hourly · alpha excluded
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {profitTs.isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <div className={`tabular text-3xl font-semibold ${
              !hasFills ? "text-muted" : total >= 0 ? "text-good" : "text-bad"
            }`}>
              {hasFills ? `${total >= 0 ? "+" : "−"}$${Math.abs(total).toFixed(2)}` : "$0.00"}
            </div>
            {chips.length > 0 ? (
              <div className="flex flex-wrap gap-2 text-xs">
                {chips.map(([src, val]) => (
                  <Badge key={src} tone={val >= 0 ? "good" : "bad"}>
                    {SOURCE_LABELS[src] ?? src}: {val >= 0 ? "+" : ""}${val.toFixed(2)}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-xs text-muted">
                No closed trades yet today — engines tick on; fills land here as they close.
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
